import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import {
  DeviceLabels,
  MeetingStatus,
  UserActivityProvider,
  useMeetingEvent,
  useMeetingManager,
  useMeetingStatus,
  MeetingManagerJoinOptions
} from 'amazon-chime-sdk-component-library-react'
import { useSnackbar } from 'notistack'

import NoRemoteVideo from '@/components/ui/meeting/NoRemoteVideo'
import AttendeeControl from '@/components/chime-sdk/controls/AttendeeControl'
import AudioInputControl from '@/components/chime-sdk/controls/AudioInputControl'
import AudioOutputControl from '@/components/chime-sdk/controls/AudioOutputControl'
import ControlBar from '@/components/chime-sdk/controls/ControlBar'
import DeviceSelectionControl from '@/components/chime-sdk/controls/DeviceSelectionControl'
import ScreenShareControl from '@/components/chime-sdk/controls/ScreenShareControl'
import VideoInputControl from '@/components/chime-sdk/controls/VideoInputControl'
import VideoTileGrid from '@/components/chime-sdk/grid/VideoTileGrid'

import ScreenShotControl from './containers/ScreenShotControl'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAccessToken, setAccessToken } from '@/redux/reducers/auth.reducer'
import { getConsultation, joinMeeting, selectAttendeeInfo, selectConsultationById, selectMeetingInfo } from '@/redux/reducers/consultation.reducer'

import './IntegratedMeeting.scss'
import { useNonDefaultDeviceChime } from '@/hooks/useNonDefaultDeviceChime'

export default function IntegratedMeeting() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const meetingEvent = useMeetingEvent()
  const meetingManager = useMeetingManager()
  const meetingStatus = useMeetingStatus()
  const { enqueueSnackbar } = useSnackbar()

  const { consultationId, userId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return {
      consultationId: searchParams.get('consultationId'),
      userId: searchParams.get('userId')
    }
  }, [location.search])

  const accessToken = useAppSelector(selectAccessToken)
  const consultation = useAppSelector(state => consultationId ? selectConsultationById(state, consultationId) : undefined)
  const attendeeInfo = useAppSelector(state => selectAttendeeInfo(state, 'visioConference'))
  const meetingInfo = useAppSelector(state => selectMeetingInfo(state, 'visioConference'))

  const startMeeting = useCallback(async (meetingInfo: any, attendeeInfo: any) => {
    const meetingSessionConfiguration = new MeetingSessionConfiguration(meetingInfo, attendeeInfo)
    const options: MeetingManagerJoinOptions = {
      deviceLabels: DeviceLabels.AudioAndVideo,
      enableWebAudio: true
    }
    await meetingManager.join(meetingSessionConfiguration, options)
    await meetingManager.start()
  }, [meetingManager])

  const endMeeting = useCallback(async () => {
    await meetingManager.leave()
  }, [meetingManager])

  useNonDefaultDeviceChime()

  useEffect(() => {
    window.parent.postMessage('loaded', '*')
    function handleReceivedMessage(e: MessageEvent) {
      const data = e.data
      if (data.accessToken) {
        dispatch(setAccessToken(data.accessToken))
      }
    }
    window.addEventListener('message', handleReceivedMessage)
    return () => {
      window.removeEventListener('message', handleReceivedMessage)
      endMeeting()
    }
    // eslint-disable-next-line
  }, [])

  // join meeting in order to get meeting and attendee info
  useEffect(() => {
    if (consultationId && userId) {
      // TODO: place getConsultation here when we would have implemented authentication
      // dispatch(getConsultation(consultationId))
      dispatch(joinMeeting({ roomOrConsultationId: consultationId, userId, meetingType: 'visioConference' }))
    }
  }, [dispatch, consultationId, userId])

  // TODO: remove this effect when we would have implemented authentication
  useEffect(() => {
    if (consultationId && accessToken && !consultation) {
      dispatch(getConsultation(consultationId))
    }
  }, [dispatch, accessToken, consultationId, consultation])

  useEffect(() => {
    if (meetingInfo && attendeeInfo) {
      startMeeting(meetingInfo, attendeeInfo)
    }
  }, [startMeeting, attendeeInfo, meetingInfo])

  // reconnect to a new meeting which is ended if a single attendee has been connecting but with no audio state changes for 30 minutes
  useEffect(() => {
    if (
      meetingEvent?.name === 'meetingEnded' &&
      meetingStatus !== MeetingStatus.Left && meetingStatus !== MeetingStatus.JoinedFromAnotherDevice &&
      consultationId && userId
    ) {
      dispatch(joinMeeting({ roomOrConsultationId: consultationId, userId, meetingType: 'visioConference' }))
    }
  }, [dispatch, consultationId, userId, meetingEvent, meetingStatus])

  useEffect(() => {
    switch (meetingStatus) {
      case MeetingStatus.JoinedFromAnotherDevice:
        enqueueSnackbar(t('snackbar.info.meeting.joinedFromAnotherDevice'), { preventDuplicate: true, variant: 'info', persist: true })
        break
      default:
        break
    }
  }, [enqueueSnackbar, t, meetingStatus])

  return (
    <UserActivityProvider>
      <div className="integrated-meeting">
        <VideoTileGrid layout="featured" noRemoteVideoView={<NoRemoteVideo />} />
        {meetingStatus === MeetingStatus.Succeeded && (
          <>
            <ControlBar>
              <AudioInputControl />
              <VideoInputControl />
              <AudioOutputControl />
              <DeviceSelectionControl />
            </ControlBar>
            <ControlBar anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
              <ScreenShareControl sx={{ borderTopRightRadius: 'unset', borderBottomRightRadius: 'unset' }} />
              <ScreenShotControl sx={{ borderRadius: 'unset' }} />
              <AttendeeControl sx={{ borderTopLeftRadius: 'unset', borderBottomLeftRadius: 'unset' }} />
            </ControlBar>
          </>
        )}
      </div>
    </UserActivityProvider>
  )
}